import { convertFromUTcToTimezone } from "@/components/ds/lib/DSDateUtils";
import { formatDateToLocal__Date } from "@/lib/dateUtils";
import { DayOfWeek, PlayerDetails } from "@/lib/types";
import {
  getChannelDetails,
  getChannelDetailItems,
  getChannelDetailDate,
  getCampaignDetails,
  getCampaignDetailItems,
  getCampaignDetailDate,
  getPlayerCampaign,
  getChannelDetailContent,
  getCampaignDetailContent,
  getAreaDetails,
} from "@/sideEffects/api";
import { format } from "date-fns";

interface PlayerScheduleItemDateContent {
  contentintId?: number;
  contentGroupintId?: number;
}

interface PlayerScheduleItem {
  periodStart: Date;
  periodEnd: Date;
  dates: {
    dayOfWeek: DayOfWeek;
    startTime: string;
    endTime: string;
  }[];
  contents: PlayerScheduleItemDateContent[];
}

export interface PlayerScheduleConf {
  name: string;
  items: PlayerScheduleItem[];
}

export interface PlayerSchedule {
  channelConf?: PlayerScheduleConf;
  campaignConfs?: PlayerScheduleConf[];
  isOverridenByArea?: boolean;
}

export const constructChannelSchedule = async (
  assignedChannelId: string,
  firmGuid: string,
  timezone: string
): Promise<PlayerScheduleConf> => {
  const channelDetails = (await getChannelDetails(assignedChannelId, firmGuid)).data;
  const channelConf: PlayerScheduleConf = {
    name: channelDetails.name,
    items: [],
  };

  if (channelDetails) {
    const channelDetailItems = (await getChannelDetailItems(assignedChannelId, firmGuid)).data.result || [];
    for (const channelDetailItem of channelDetailItems) {
      const items: PlayerScheduleItem = {
        periodStart: convertFromUTcToTimezone(channelDetailItem.periodStart, timezone),
        periodEnd: convertFromUTcToTimezone(channelDetailItem.periodEnd, timezone),
        dates: [],
        contents: [],
      };

      const channelDetailItemDate =
        (
          await getChannelDetailDate(
            {
              numberOfObjectsPerPage: 999999,
              pageNumber: 1,
            },
            { Firm_Id: firmGuid, ChannelDetail_Id: channelDetailItem.id }
          )
        ).data.result || [];

      for (const date of channelDetailItemDate) {
        items.dates.push({
          dayOfWeek: date.dayOfWeek,
          startTime: format(convertFromUTcToTimezone(date.startTime, timezone), "HH:mm"),
          endTime: format(convertFromUTcToTimezone(date.endTime, timezone), "HH:mm"),
        });
      }

      const contents = (await getChannelDetailContent(channelDetailItem.id, firmGuid)).data.result || [];
      if (contents.length) {
        items.contents = contents.map(
          (r) =>
            ({
              contentintId: r.contentintId,
              contentGroupintId: r.contentGroupintId,
            } satisfies PlayerScheduleItemDateContent)
        );
      }

      channelConf.items.push(items);
    }

    // Sort items by periodStart date, past dates first
    channelConf.items.sort((a, b) => {
      const dateA = new Date(a.periodStart);
      const dateB = new Date(b.periodStart);
      return dateA.getTime() - dateB.getTime();
    });
  }

  return channelConf;
};

export const constructCampaignSchedule = async (
  assignedCampaignId: string,
  firmGuid: string,
  timezone: string
): Promise<PlayerScheduleConf> => {
  const campaignDetails = (await getCampaignDetails(assignedCampaignId, firmGuid)).data;
  const campaignConf: PlayerScheduleConf = {
    name: campaignDetails.name,
    items: [],
  };

  if (campaignDetails) {
    const campaignDetailItems = (await getCampaignDetailItems(assignedCampaignId, firmGuid)).data.result || [];
    for (const campaignDetailItem of campaignDetailItems) {
      const items: PlayerScheduleItem = {
        periodStart: convertFromUTcToTimezone(campaignDetailItem.periodStart, timezone),
        periodEnd: convertFromUTcToTimezone(campaignDetailItem.periodEnd, timezone),
        dates: [],
        contents: [],
      };

      const campaignDetailItemDate =
        (
          await getCampaignDetailDate(
            {
              numberOfObjectsPerPage: 999999,
              pageNumber: 1,
            },
            { Firm_Id: firmGuid, CampaignDetail_Id: campaignDetailItem.id }
          )
        ).data.result || [];

      for (const date of campaignDetailItemDate) {
        items.dates.push({
          dayOfWeek: date.dayOfWeek,
          startTime: format(convertFromUTcToTimezone(date.startTime, timezone), "HH:mm"),
          endTime: format(convertFromUTcToTimezone(date.endTime, timezone), "HH:mm"),
        });
      }

      const contents = (await getCampaignDetailContent(campaignDetailItem.id, firmGuid)).data.result || [];
      if (contents.length) {
        items.contents = contents.map(
          (r) =>
            ({
              contentintId: r.contentintId,
            } satisfies PlayerScheduleItemDateContent)
        );
      }
      campaignConf.items.push(items);
    }

    // Sort items by periodStart date, past dates first
    campaignConf.items.sort((a, b) => {
      const dateA = new Date(a.periodStart);
      const dateB = new Date(b.periodStart);
      return dateA.getTime() - dateB.getTime();
    });
  }

  return campaignConf;
};

export const constructPlayerSchedule = async (playerDetails: PlayerDetails, firmGuid: string, timezone: string) => {
  const constructedSchedule: PlayerSchedule = {};
  const assignedChannelId = playerDetails.channelId;

  if (playerDetails.areaId) {
    const areaDetails = (await getAreaDetails(playerDetails.areaId, firmGuid)).data;
    if (areaDetails.overwriteSub) {
      constructedSchedule.isOverridenByArea = true;
    }
  }

  if (assignedChannelId) {
    constructedSchedule.channelConf = await constructChannelSchedule(assignedChannelId, firmGuid, timezone);
  }

  const assignedCampaignIds = ((await getPlayerCampaign(playerDetails.id, firmGuid)).data.result || []).map(
    (r) => r.campaignId
  );
  for (const assignedCampaignId of assignedCampaignIds) {
    if (!constructedSchedule.campaignConfs) {
      constructedSchedule.campaignConfs = [];
    }
    constructedSchedule.campaignConfs.push(await constructCampaignSchedule(assignedCampaignId, firmGuid, timezone));
  }

  return constructedSchedule;
};

// export const constructAreaSchedule = async (areaDetails: AreaDetails, firmGuid: string) => {
//   const constructedSchedule: PlayerSchedule = {};
//   const assignedChannelId = areaDetails.channelId;

//   if (areaDetails.overwriteSub) {
//     constructedSchedule.isOverridenByArea = true;
//   }

//   if (assignedChannelId) {
//     constructedSchedule.channelConf = await constructChannelSchedule(assignedChannelId, firmGuid);
//   }

//   const assignedCampaignIds = ((await getAreaCampaign(areaDetails.id, firmGuid)).data.result || []).map(
//     (r) => r.campaignId
//   );
//   for (const assignedCampaignId of assignedCampaignIds) {
//     if (!constructedSchedule.campaignConfs) {
//       constructedSchedule.campaignConfs = [];
//     }
//     constructedSchedule.campaignConfs.push(await constructCampaignSchedule(assignedCampaignId, firmGuid));
//   }

//   return constructedSchedule;
// };
